import { Link } from 'react-router';

import { useAuth } from '../../auth/AuthContext';
import { hasAtLeast } from '../../auth/roles';
import { useApplicationsCount } from '../../api/applications';

import {
  UNLINKED_ALERT_QUERIES,
  UNLINKED_ALERT_TEXT_TEST_ID,
  unlinkedAlert,
  type UnlinkedAlertStatus,
} from './unlinked-alert-model';

/**
 * The red «несвязанные ученики» alert above the register (T.Z20b).
 *
 * The dashboard's «Несвязанные» card (T.Z20) became this: the owner does not want
 * the bucket as a number on a grid, he wants it as a condition a person acts on —
 * and the acting happens HERE, over the register. So the component counts the two
 * offline buckets through the same `#3` slices its own link answers (one request
 * per bucket, a page of one, `meta.total`), picks the first non-empty one in funnel
 * order and renders ONE clickable red banner. Both empty (or both counts still in
 * flight / failed) render nothing at all — an unread count never reads as «всё
 * связали».
 *
 * The banner is offered to ADMIN and МЕНЕДЖЕР only: the register itself stays
 * readable for every role, but only staff can attach a площадка, and a curator
 * session never sends the two count requests either.
 */
export function UnlinkedAlert() {
  const { role } = useAuth();
  // `enabled` rather than an early return: the hooks stay unconditional, and a
  // curator's session does not put the two counts on the wire at all.
  const staff = hasAtLeast(role, 'MANAGER');
  // The two candidate buckets, written out and in funnel order (`LINK_RECEIVED`
  // first). The queries come from the model — the same list that builds the
  // drill-down hrefs, so a count and the link it arms can never drift apart.
  const linkReceived = useApplicationsCount(UNLINKED_ALERT_QUERIES[0]!, { enabled: staff });
  const eviPassed = useApplicationsCount(UNLINKED_ALERT_QUERIES[1]!, { enabled: staff });

  const alert = unlinkedAlert([
    { status: 'LINK_RECEIVED' satisfies UnlinkedAlertStatus, total: linkReceived.data },
    { status: 'EVI_PASSED' satisfies UnlinkedAlertStatus, total: eviPassed.data },
  ]);

  if (!staff || alert === null) return null;

  return (
    <Link
      to={alert.href}
      className="unlinked-alert"
      data-testid={alert.testId}
      title={alert.hint}
      role="alert"
    >
      <span className="unlinked-alert__marker" aria-hidden="true">
        !
      </span>
      <span className="unlinked-alert__text" data-testid={UNLINKED_ALERT_TEXT_TEST_ID}>
        {alert.text}
      </span>
      <span className="unlinked-alert__cta" aria-hidden="true">
        Открыть реестр →
      </span>
    </Link>
  );
}
